<!-- src/views/admin/AdminLayout.vue -->
<template>
  <div class="admin-layout" :class="{ 'sidebar-open': sidebarOpen }">
    <button
      v-if="sidebarOpen"
      class="sidebar-backdrop"
      type="button"
      aria-label="메뉴 닫기"
      @click="sidebarOpen = false"
    ></button>

    <aside class="admin-sidebar">
      <div class="sidebar-logo-card">
        <img
          src="@/assets/icon/tech-center-icon.jpg"
          alt="부산광역시교육청 로고"
        />
        <div>
          <strong>테크센터</strong>
          <span>ADMIN CONSOLE</span>
        </div>
      </div>

      <nav class="admin-nav" aria-label="관리자 메뉴">
        <RouterLink class="nav-item" to="/admin/time" @click="closeSidebar">
          <span class="nav-icon" aria-hidden="true">◷</span>
          <span>전자칠판 관리시스템</span>
        </RouterLink>
        <RouterLink class="nav-item" to="/admin/manage" @click="closeSidebar">
          <span class="nav-icon" aria-hidden="true">▦</span>
          <span>관리카드</span>
        </RouterLink>
        <RouterLink class="nav-item" to="/admin/user" @click="closeSidebar">
          <span class="nav-icon" aria-hidden="true">◉</span>
          <span>담당자 관리</span>
        </RouterLink>
      </nav>

      <div class="sidebar-footer">
        <span class="status-dot"></span>
        <span>System online</span>
      </div>
    </aside>

    <div class="admin-main">
      <header class="topbar">
        <div class="brand-area">
          <button
            class="menu-toggle"
            type="button"
            aria-label="관리자 메뉴 열기"
            @click="sidebarOpen = true"
          >
            ☰
          </button>
          <div class="brand-text">
            <div class="brand-title">부산광역시교육청 테크센터 - 관리자</div>
            <div class="brand-sub">(주) 디아이앤씨</div>
          </div>
        </div>

        <div class="admin-actions">
          <OveruseNotifications
            :devices="overuseState.devices"
            :loading="overuseState.loading"
            :error="overuseState.error"
            :has-loaded="overuseState.hasLoaded"
            @open="overusePoller.onBellClick"
            @detail="notificationDetail = $event"
          />
          <div class="user-card" title="현재 로그인 사용자">
            <span class="user-avatar">{{ userInitial }}</span>
            <div class="user-info">
              <span class="user-label">ADMINISTRATOR</span>
              <strong>{{ userLabel }}</strong>
            </div>
          </div>
          <button class="action-btn" type="button" @click="chgPwdModal">
            비밀번호 변경
          </button>
          <button class="action-btn danger" type="button" @click="logout">
            로그아웃
          </button>
        </div>
      </header>

      <main class="content">
        <RouterView />
      </main>
    </div>

    <DeviceDetailHost
      v-if="notificationDetail"
      :detail-only="true"
      :initial-device="notificationDetail"
      @detail-close="notificationDetail = null"
      @device-updated="overusePoller.refresh"
    />

    <!-- 비밀번호 변경 모달 -->
    <div v-if="showChgPwd" class="modal-overlay" @click.self="closeChgPwd">
      <div class="modal">
        <div class="modal-title">비밀번호 변경</div>

        <div>
          <label class="field">
            <div class="label">현재 비밀번호</div>
            <input
              type="password"
              v-model.trim="chgForm.nowPwd"
              class="input mono"
            />
            <small v-if="chgErrors.nowPwd" class="error">{{
              chgErrors.nowPwd
            }}</small>
          </label>

          <label class="field">
            <div class="label">새 비밀번호</div>
            <input
              type="password"
              v-model.trim="chgForm.pwd"
              class="input mono"
            />
            <small v-if="chgErrors.pwd" class="error">{{
              chgErrors.pwd
            }}</small>
          </label>

          <label class="field">
            <div class="label">새 비밀번호 확인</div>
            <input
              type="password"
              v-model.trim="chgForm.pwdChk"
              class="input mono"
            />
            <small v-if="chgErrors.pwdChk" class="error">{{
              chgErrors.pwdChk
            }}</small>
          </label>
        </div>

        <div class="modal-actions">
          <button class="btn btn-ghost" type="button" @click="closeChgPwd">
            취소
          </button>
          <button class="btn btn-primary" type="button" @click="chgPwd">
            변경
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  computed,
  ref,
  onMounted,
  onBeforeUnmount,
  reactive,
  inject,
  defineAsyncComponent
} from "vue";
import { useRouter } from "vue-router";
import Cookies from "js-cookie";
import { memberApi, deviceApi } from "@/api";
import OveruseNotifications from "@/components/admin/OveruseNotifications.vue";
import { createDevice24Poller } from "@/utils/device24Poller";
import { filterVisibleDevices } from "@/utils/deviceVisibility";
const DeviceDetailHost = defineAsyncComponent(() =>
  import("@/views/admin/TimeView.vue")
);
const notificationDetail = ref(null);
const overuseState = reactive({
  devices: [],
  loading: false,
  error: "",
  hasLoaded: false
});
const overusePoller = createDevice24Poller({
  fetchDevices: async (options) =>
    filterVisibleDevices(await deviceApi.getDevice24(options)),
  publish: (patch) => Object.assign(overuseState, patch)
});
onMounted(overusePoller.start);
onBeforeUnmount(overusePoller.stop);

// ======================================================================

// ======================================================================

/**
 * ✅ Toast
 */
const showToast = inject("showToast");

/**
 * ✅ 여기서는 accessToken을 localStorage에 저장한다는 가정
 * - 실제로는 Pinia auth store로 바꾸는 걸 권장
 */
const router = useRouter();

// 샘플 사용자 라벨 (원하면 로그인 응답으로 사용자명 저장해서 표시)
const userLabel = computed(() => {
  const u_name = Cookies.get("u_name");
  return u_name || "(이름 없음)";
});

const userInitial = computed(() => userLabel.value.trim().charAt(0) || "A");
const sidebarOpen = ref(false);

function closeSidebar() {
  sidebarOpen.value = false;
}

// 샘플 그룹 데이터 (API 연동 전)
// const groups = ref([]);
// async function refreshGroups() {
//   const result = await groupApi.getGroup();
//   groups.value = result;
// }

function logout() {
  overusePoller.stop();
  notificationDetail.value = null;
  // 모든 쿠키 가져오기
  const allCookies = Cookies.get();
  // 각 쿠키를 순회하며 삭제
  for (const cookieName in allCookies) {
    Cookies.remove(cookieName);
  }
  router.replace("/admin");
}

// ======================================================================

const showChgPwd = ref(false);

const chgForm = reactive({
  nowPwd: "",
  pwd: "",
  pwdChk: ""
});
const chgErrors = reactive({
  nowPwd: "",
  pwd: "",
  pwdChk: ""
});

function chgPwdModal() {
  showChgPwd.value = true;
  chgForm.nowPwd = "";
  chgForm.pwd = "";
  chgForm.pwdChk = "";
  chgErrors.nowPwd = "";
  chgErrors.pwd = "";
  chgErrors.pwdChk = "";
}

function closeChgPwd() {
  showChgPwd.value = false;
}

async function chgPwd() {
  chgErrors.nowPwd = "";
  chgErrors.pwd = "";
  chgErrors.pwdChk = "";

  if (!chgForm.nowPwd || chgForm.nowPwd.length < 4) {
    chgErrors.nowPwd = "현재 비밀번호는 4자 이상 입력해주세요.";
  }
  if (!chgForm.pwd || chgForm.pwd.length < 4) {
    chgErrors.pwd = "새 비밀번호는 4자 이상 입력해주세요.";
  }
  if (chgForm.pwd !== chgForm.pwdChk) {
    chgErrors.pwdChk = "새 비밀번호와 새 비밀번호 확인이 서로 맞지 않습니다.";
  }

  if (chgErrors.nowPwd || chgErrors.pwd || chgErrors.pwdChk) return;

  const result = await memberApi.chgPassword(chgForm.nowPwd, chgForm.pwd);
  if (result) {
    closeChgPwd();
    showToast?.("변경되었습니다.");
  }
}
</script>

<style scoped>
.sidebar-logo-card strong {
  font-size: 19px;
  font-weight: 850;
  line-height: 1.1;
  letter-spacing: -0.04em;
}

.sidebar-logo-card span {
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.12em;
}

.admin-layout {
  margin: 0px;
  padding: 0px;
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f6f7f9;
  color: #111;
  font-family: system-ui, -apple-system, Segoe UI, Roboto, "Noto Sans KR", Arial,
    sans-serif;
}

/* Topbar */
.topbar {
  height: 82px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 34px;
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
}

.brand-area {
  display: flex;
  align-items: center;
  gap: 16px;
}

.brand-icon {
  width: 54px;
  height: 54px;
  border-radius: 14px;
  overflow: hidden;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
}

.brand-icon img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.brand-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.brand-title {
  font-size: 24px;
  font-weight: 800;
  color: #111827;
  line-height: 1.1;
}

.brand-sub {
  font-size: 13px;
  color: #6b7280;
  line-height: 1;
}

.brand {
  font-weight: 800;
  letter-spacing: -0.2px;
}

/* Header Right */
.admin-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.user-card {
  height: 42px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 14px 0 10px;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  background: #f9fafb;
}

.user-avatar {
  width: 28px;
  height: 28px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #111827;
  color: #fff;
  font-size: 13px;
}

.user-info {
  display: flex;
  flex-direction: column;
  line-height: 1.1;
}

.user-info .user-label {
  font-size: 11px;
  color: #6b7280;
}

.user-info strong {
  max-width: 120px;
  font-size: 13px;
  color: #111827;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.action-btn {
  height: 38px;
  padding: 0 14px;
  border-radius: 999px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #374151;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.action-btn:hover {
  background: #f3f4f6;
  border-color: #9ca3af;
}

.action-btn.danger {
  color: #b42318;
  border-color: #f1c0c0;
  background: #fffafa;
}

.action-btn.danger:hover {
  background: #fff1f1;
}

/* Admin Menu */
.admin-nav {
  height: 54px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 34px;
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
}

.nav-item {
  height: 36px;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 0 16px;
  border-radius: 999px;
  text-decoration: none;
  color: #4b5563;
  font-size: 14px;
  font-weight: 700;
}

.nav-item:hover {
  background: #f3f4f6;
  color: #111827;
}

.nav-item.router-link-active {
  background: #111827;
  color: #ffffff;
}

.nav-icon {
  font-size: 14px;
}

/* 모바일 */
@media (max-width: 900px) {
  .topbar {
    height: auto;
    padding: 14px;
    align-items: flex-start;
    gap: 12px;
    flex-direction: column;
  }

  .brand-title {
    font-size: 18px;
  }

  .admin-actions {
    width: 100%;
    flex-wrap: wrap;
  }

  .user-card {
    max-width: 100%;
  }

  .admin-nav {
    height: auto;
    padding: 10px 14px;
    overflow-x: auto;
  }

  .nav-item {
    flex: 0 0 auto;
  }
}

/* Body */
.body {
  flex: 1;
  min-height: 0;
  display: flex;
}

/* Sidebar */
.sidebar {
  width: 280px;
  background: #fff;
  border-right: 1px solid #e6e8eb;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.sidebar-header {
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 12px;
  border-bottom: 1px solid #eef0f2;
}

.sidebar-title {
  font-weight: 700;
}

.sidebar-content {
  padding: 10px 8px;
  overflow: auto;
}

.tree {
  list-style: none;
  padding: 0;
  margin: 0;
}

.tree-item {
  margin: 2px 0;
}

.tree-link {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 10px;
  text-decoration: none;
  color: #222;
}

.tree-link:hover {
  background: #f3f4f6;
}

.tree-link.router-link-active {
  background: #eef2ff;
  color: #1f2a6d;
}

.folder {
  width: 18px;
  display: inline-flex;
  justify-content: center;
}

.name {
  flex: 1;
}

.count {
  font-size: 12px;
  color: #666;
  border: 1px solid #e6e8eb;
  padding: 2px 8px;
  border-radius: 999px;
  background: #fff;
}

.tree-divider {
  height: 1px;
  background: #eef0f2;
  margin: 8px 0;
}

/* Content */
.content {
  flex: 1;
  min-width: 0;
  padding: 14px;
  overflow: auto;
}

/* Buttons */
.btn {
  border: 1px solid transparent;
  border-radius: 10px;
  padding: 8px 10px;
  font-size: 13px;
  cursor: pointer;
  background: #111;
  color: #fff;
}

.btn-outline {
  background: #fff;
  border-color: #e6e8eb;
  color: #111;
}

.btn-outline:hover {
  background: #f3f4f6;
}

.btn-ghost {
  background: transparent;
  border-color: transparent;
  color: #555;
  padding: 6px 8px;
}

.btn-ghost:hover {
  background: #f3f4f6;
}

/* modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  display: grid;
  place-items: center;
  padding: 20px;
  z-index: 50;
}

.modal {
  width: 300px;
  max-width: 100%;
  background: #fff;
  border-radius: 16px;
  padding: 16px;
  border: 1px solid #e6e8eb;
}

.modal.wide {
  width: 860px;
}

.modal-title {
  font-size: 16px;
  font-weight: 900;
  margin-bottom: 12px;
}

.grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.field {
  display: grid;
  gap: 6px;
  margin-top: 10px;
}

.label {
  font-size: 13px;
  color: #555;
}

.error {
  color: #d93025;
  font-size: 12px;
}

.hint {
  margin-top: 10px;
  font-size: 12px;
  color: #666;
}

.modal-actions {
  margin-top: 14px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.reset-summary {
  display: grid;
  grid-template-columns: 60px 1fr;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid #eef0f2;
  border-radius: 12px;
  background: #fafbfc;
  margin-bottom: 10px;
}

.reset-summary .k {
  font-size: 12px;
  color: #666;
}

.warning {
  border: 1px solid #eef0f2;
  background: #fafbfc;
  border-radius: 12px;
  padding: 10px 12px;
  font-size: 13px;
  color: #333;
}

.warning.danger {
  border-color: #f1c0c0;
  background: #fff5f5;
  color: #b42318;
}

.brand-icon {
  width: 54px;
  height: 54px;
  border-radius: 14px;
  overflow: hidden;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
}

.brand-icon img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.brand-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.brand-title {
  font-size: 24px;
  font-weight: 800;
  color: #111827;
  line-height: 1.1;
}

.brand-sub {
  font-size: 13px;
  color: #6b7280;
  line-height: 1;
}
@media (max-width: 560px) {
  .topbar {
    flex-wrap: wrap;
    gap: 12px;
  }
  .admin-actions {
    margin-left: auto;
    max-width: 100%;
  }
}
</style>
